import { getWorkflowTaskNameFromType } from "#src/components/Workflows/WorkflowsHelpers";
import {
  AssessTemplatedReportForCommentsAction,
  AssetType,
  ChoiceTask,
  EventStatus,
  FormChoiceAction,
  FormSubmissionStatus,
  SystemActionType,
  UserTaskType,
  VariableChoiceAction,
  WorkflowConfigStepType,
  WorkflowConfigurationType,
  WorkflowParallelTasksType,
  WorkflowStepType,
  WorkflowTemplateApiVersion,
  WorkflowTriggerEvent,
  WorkflowTriggerEventType,
  WorkflowTriggerTimePeriod,
  WorkflowTriggerType,
  type WorkflowTemplateApiVersionType,
  type WorkflowTriggerTypeType,
} from "@validereinc/domain";
import { Draft } from "immer";
import { nanoid } from "nanoid";
import { DEFAULT_CRON } from "./WorkflowConfigurationFormStore";
import { NewNodeDetailsType } from "./WorkflowStepListItem";

export type WorkflowTriggerOptionType = {
  type: WorkflowTriggerTypeType;
  event?: WorkflowTriggerEventType;
};

const WORKFLOW_TRIGGER_OPTIONS: WorkflowTriggerOptionType[] = [
  {
    type: WorkflowTriggerType.SCHEDULED,
  },
  {
    type: WorkflowTriggerType.SYSTEM,
    event: WorkflowTriggerEvent.FORM_SUBMISSION_CREATE,
  },
  {
    type: WorkflowTriggerType.SYSTEM,
    event: WorkflowTriggerEvent.ASSET_CREATE,
  },
  {
    type: WorkflowTriggerType.SYSTEM,
    event: WorkflowTriggerEvent.ASSET_UPDATE,
  },
  {
    type: WorkflowTriggerType.SYSTEM,
    event: WorkflowTriggerEvent.WORKFLOW_COMPLETE,
  },
  {
    type: WorkflowTriggerType.SYSTEM,
    event: WorkflowTriggerEvent.DATASET_SUBMISSION,
  },
];

/**
 * Returns trigger options available for a template's API version.
 * v1 templates exclude dataset submission; v2 templates only allow dataset submission.
 */
export const getWorkflowTriggerOptionsForApiVersion = (
  apiVersion?: WorkflowTemplateApiVersionType
): WorkflowTriggerOptionType[] => {
  const isV2Template = apiVersion === WorkflowTemplateApiVersion.V2;

  if (isV2Template) {
    return WORKFLOW_TRIGGER_OPTIONS.filter(
      (trigger) => trigger.event === WorkflowTriggerEvent.DATASET_SUBMISSION
    );
  }

  return WORKFLOW_TRIGGER_OPTIONS.filter(
    (trigger) => trigger.event !== WorkflowTriggerEvent.DATASET_SUBMISSION
  );
};

type CreateManualStepsResult = {
  manualStep1Id: string;
  manualStep2Id: string;
  manualStep1: WorkflowConfigStepType;
  manualStep2: WorkflowConfigStepType;
};

/**
 * Creates two manual steps for choice steps
 */
export const createManualStepsForChoice = (
  previousStepNext?: string
): CreateManualStepsResult => {
  const manualStep1Id = `manual-step-${nanoid(8)}`;
  const manualStep2Id = `manual-step-${nanoid(8)}`;

  const manualStep1: WorkflowConfigStepType = {
    name: getWorkflowTaskNameFromType(UserTaskType.MANUAL_TASK),
    type: WorkflowStepType.USER_TASK,
    description: "Description",
    assignee: null,
    assignee_user: null,
    assignee_group: null,
    task: {
      type: UserTaskType.MANUAL_TASK,
    },
    ...(previousStepNext ? { next: previousStepNext } : { end: true }),
  } as WorkflowConfigStepType;

  const manualStep2: WorkflowConfigStepType = {
    name: getWorkflowTaskNameFromType(UserTaskType.MANUAL_TASK),
    type: WorkflowStepType.USER_TASK,
    description: "Description",
    assignee: null,
    assignee_user: null,
    assignee_group: null,
    task: {
      type: UserTaskType.MANUAL_TASK,
    },
    end: true,
  } as WorkflowConfigStepType;

  return {
    manualStep1Id,
    manualStep2Id,
    manualStep1,
    manualStep2,
  };
};

/**
 * Sets up choices array for a new choice step
 */
export const setupChoiceStepChoices = (
  newStep: Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>,
  manualStep1Id: string,
  manualStep2Id: string
): void => {
  if (isUserChoiceStep(newStep)) {
    (newStep.task as ChoiceTask).choices = [
      {
        id: `manual-task-choice-${nanoid(8)}`,
        name: "Choice 1",
        description: "Description",
        next: manualStep1Id,
      },
      {
        id: `manual-task-choice-${nanoid(8)}`,
        name: "Choice 2",
        description: "Description",
        next: manualStep2Id,
      },
    ];
  } else if (isFormChoiceStep(newStep)) {
    (newStep.task as FormChoiceAction).choices = [
      {
        answer_filter: {},
        next: manualStep1Id,
      },
      {
        answer_filter: {},
        next: manualStep2Id,
      },
    ];
  } else if (isVariableChoiceStep(newStep)) {
    (newStep.task as VariableChoiceAction).choices = [
      {
        condition: {},
        next: manualStep1Id,
      },
      {
        condition: {},
        next: manualStep2Id,
      },
    ];
  }
};

/**
 * Sets up next steps for a new assess templated report for comments step
 */
export const setupAssessTemplatedReportForCommentsStepNextSteps = (
  newStep: Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>,
  manualStep1Id: string,
  manualStep2Id: string
): void => {
  (
    newStep.task as AssessTemplatedReportForCommentsAction
  ).assessment_failure_next = manualStep1Id;
  (
    newStep.task as AssessTemplatedReportForCommentsAction
  ).assessment_success_next = manualStep2Id;
};

/**
 * Updates a previous choice step by adding a new choice to its choices array
 */
export const addChoiceToPreviousStep = (
  previousStep: Draft<
    Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>
  >,
  newStepId: string,
  newStep: Draft<Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>>,
  nextStepId: string | undefined
): void => {
  if (isUserChoiceStep(previousStep)) {
    newStep.end = true;
    const newChoiceId = `${newStepId}-choice-${nanoid(8)}`;
    const newChoice = {
      id: newChoiceId,
      name: newStep.name ?? "New Choice",
      description: newStep.description ?? "",
      next: newStepId,
    };
    (previousStep.task as ChoiceTask).choices.push(newChoice);
  } else if (isFormChoiceStep(previousStep)) {
    newStep.end = true;
    const newChoice = {
      answer_filter: {},
      next: newStepId,
    };
    (previousStep.task as FormChoiceAction).choices.push(newChoice);
  } else if (isVariableChoiceStep(previousStep)) {
    newStep.end = true;
    const newChoice = {
      condition: {},
      next: newStepId,
    };
    (previousStep.task as VariableChoiceAction).choices.push(newChoice);
  } else if (isAssessTemplatedReportForCommentsStep(previousStep)) {
    const previousTask =
      previousStep.task as AssessTemplatedReportForCommentsAction;
    if (
      previousTask.assessment_success_next &&
      previousTask.assessment_success_next === nextStepId
    ) {
      previousTask.assessment_success_next = newStepId;
    } else if (
      previousTask.assessment_failure_next &&
      previousTask.assessment_failure_next === nextStepId
    ) {
      previousTask.assessment_failure_next = newStepId;
    }
  }
};

/**
 * Adds a new branch to a parallel step.
 * The new step becomes the starting step of the new branch
 * Returns the branch step ID so it can be marked as dirty
 */
export const addBranchToParallelStep = (
  parallelStep: Draft<WorkflowParallelTasksType>,
  newStepId: string,
  newStep: Draft<Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>>,
  markNodeAsDirty: (nodeId: string) => void
): string => {
  newStep.end = true;

  const newBranch = {
    start: newStepId,
    steps: {
      [newStepId]: newStep,
    },
  };

  parallelStep.branches.push(newBranch);
  markNodeAsDirty(newStepId);

  return newStepId;
};

/**
 * Updates a previous step by setting its end and next property
 * If the previous step is a trigger (not found in steps), updates the workflow start instead
 */
export const updatePreviousStepNext = (
  previousStep: Draft<WorkflowConfigStepType> | undefined,
  newStepId: string,
  nextStepId: string | undefined,
  newStep: Draft<WorkflowConfigStepType>,
  doesNewStepHaveChoices: boolean,
  isNewStepAssessTemplatedReportForComments: boolean,
  draft: Draft<WorkflowConfigurationType>,
  previousStepId: string
): void => {
  // Check if previous step is a trigger (not found in steps object)
  const isPreviousStepTrigger = !previousStep && !draft.steps[previousStepId];

  // If adding after a trigger, update the workflow start and connect the new step to the previous start
  if (isPreviousStepTrigger) {
    const previousStartStepId = draft.start;
    draft.start = newStepId;
    if (previousStartStepId) {
      newStep.next = previousStartStepId;
    } else if (nextStepId) {
      newStep.next = nextStepId;
    } else {
      newStep.end = true;
    }
    return;
  }

  if (!previousStep) return;

  previousStep.next = newStepId;

  if (previousStep.end) {
    if (!doesNewStepHaveChoices && !isNewStepAssessTemplatedReportForComments) {
      newStep.end = true;
    }
    previousStep.end = false;
  } else if (
    !doesNewStepHaveChoices &&
    !isNewStepAssessTemplatedReportForComments
  ) {
    newStep.next = nextStepId;
  }
};

/**
 * Finds which branch contains a given step ID, if any
 * Returns the parallel step, branch, and branch index if found
 * Recursively searches nested parallel structures
 */
export const findBranchContainingStep = (
  draft: Draft<WorkflowConfigurationType>,
  stepId: string
): {
  parallelStep: Draft<WorkflowParallelTasksType>;
  branch: Draft<{
    start: string;
    steps: Record<string, WorkflowConfigStepType>;
  }>;
  branchIndex: number;
} | null => {
  const searchInParallelStep = (
    parallelStep: Draft<WorkflowParallelTasksType>
  ): {
    parallelStep: Draft<WorkflowParallelTasksType>;
    branch: Draft<{
      start: string;
      steps: Record<string, WorkflowConfigStepType>;
    }>;
    branchIndex: number;
  } | null => {
    for (
      let branchIndex = 0;
      branchIndex < parallelStep.branches.length;
      branchIndex++
    ) {
      const branch = parallelStep.branches[branchIndex];
      if (branch.steps[stepId]) {
        return {
          parallelStep,
          branch,
          branchIndex,
        };
      }

      // Recursively search nested parallel steps within this branch
      for (const [, nestedStep] of Object.entries(branch.steps)) {
        if (nestedStep.type === WorkflowStepType.PARALLEL) {
          const nestedResult = searchInParallelStep(nestedStep);
          if (nestedResult) return nestedResult;
        }
      }
    }
    return null;
  };

  for (const [, step] of Object.entries(draft.steps)) {
    if (step.type === WorkflowStepType.PARALLEL) {
      const result = searchInParallelStep(step);
      if (result) return result;
    }
  }
  return null;
};

/**
 * Adds a new step to a specific branch of a parallel step
 */
const addStepToSpecificBranch = (
  branch: Draft<{
    start: string;
    steps: Record<string, WorkflowConfigStepType>;
  }>,
  previousStepId: string,
  newStepId: string,
  newStep: Draft<Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>>,
  nextStepId: string | undefined,
  doesNewStepHaveChoices: boolean,
  isNewStepAssessTemplatedReportForComments: boolean
): void => {
  const previousStep = branch.steps[previousStepId] as Exclude<
    WorkflowConfigStepType,
    WorkflowParallelTasksType
  >;
  if (!previousStep) return;

  if (isUserChoiceStep(previousStep)) {
    newStep.end = true;
    const newChoiceId = `${newStepId}-choice-${nanoid(8)}`;
    const newChoice = {
      id: newChoiceId,
      name: newStep.name ?? "New Choice",
      description: newStep.description ?? "",
      next: newStepId,
    };
    (previousStep.task as ChoiceTask).choices.push(newChoice);
  } else {
    previousStep.next = newStepId;

    if (previousStep.end) {
      if (
        !doesNewStepHaveChoices &&
        !isNewStepAssessTemplatedReportForComments
      ) {
        newStep.end = true;
      }
      previousStep.end = false;
    } else if (
      !doesNewStepHaveChoices &&
      !isNewStepAssessTemplatedReportForComments
    ) {
      if (nextStepId) {
        newStep.next = nextStepId;
      } else {
        newStep.end = true;
      }
    }
  }

  branch.steps[newStepId] = newStep;
};

/**
 * Updates the previous step
 */
export const updatePreviousStep = (
  draft: Draft<WorkflowConfigurationType>,
  previousStepIdForNewStep: string,
  newStepId: string,
  newStep: Draft<Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>>,
  nextStepId: string | undefined,
  doesNewStepHaveChoices: boolean,
  isNewStepAssessTemplatedReportForComments: boolean,
  markNodeAsDirty: (nodeId: string) => void
) => {
  // Check parallel branches
  const branchInfo = findBranchContainingStep(draft, previousStepIdForNewStep);
  if (branchInfo) {
    addStepToSpecificBranch(
      branchInfo.branch,
      previousStepIdForNewStep,
      newStepId,
      newStep,
      nextStepId,
      doesNewStepHaveChoices,
      isNewStepAssessTemplatedReportForComments
    );

    // When the previous step is inside a parallel branch, we already updated the
    // branch, so we should not fall through to top-level/trigger handling.
    return;
  }

  // If not found in a branch, try to find the step at the top level
  if (draft.steps[previousStepIdForNewStep]) {
    const previousStep = draft.steps[previousStepIdForNewStep];

    if (previousStep.type === WorkflowStepType.PARALLEL) {
      addBranchToParallelStep(
        previousStep,
        newStepId,
        newStep,
        markNodeAsDirty
      );
    } else if (
      isChoiceStep(previousStep) ||
      isAssessTemplatedReportForCommentsStep(previousStep)
    ) {
      addChoiceToPreviousStep(previousStep, newStepId, newStep, nextStepId);
    } else {
      updatePreviousStepNext(
        previousStep,
        newStepId,
        nextStepId,
        newStep,
        doesNewStepHaveChoices,
        isNewStepAssessTemplatedReportForComments,
        draft,
        previousStepIdForNewStep
      );
    }
  } else {
    // Previous step not found in steps - it's likely a trigger
    updatePreviousStepNext(
      undefined,
      newStepId,
      nextStepId,
      newStep,
      doesNewStepHaveChoices,
      isNewStepAssessTemplatedReportForComments,
      draft,
      previousStepIdForNewStep
    );
  }
};

export const isUserChoiceStep = (
  step: WorkflowConfigStepType
): step is WorkflowConfigStepType & {
  type: typeof WorkflowStepType.USER_TASK;
  task: ChoiceTask;
} => {
  return (
    step.type === WorkflowStepType.USER_TASK &&
    step.task.type === UserTaskType.CHOICE
  );
};

export const isFormChoiceStep = (
  step: WorkflowConfigStepType
): step is WorkflowConfigStepType & {
  type: typeof WorkflowStepType.SYSTEM_ACTION;
  task: FormChoiceAction;
} => {
  return (
    step.type === WorkflowStepType.SYSTEM_ACTION &&
    step.task.type === SystemActionType.FORM_CHOICE
  );
};

export const isVariableChoiceStep = (
  step: WorkflowConfigStepType
): step is WorkflowConfigStepType & {
  type: typeof WorkflowStepType.SYSTEM_ACTION;
  task: VariableChoiceAction;
} => {
  return (
    step.type === WorkflowStepType.SYSTEM_ACTION &&
    step.task.type === SystemActionType.VARIABLE_CHOICE
  );
};

export const isChoiceStep = (
  step: WorkflowConfigStepType
): step is WorkflowConfigStepType & {
  type:
    | typeof WorkflowStepType.USER_TASK
    | typeof WorkflowStepType.SYSTEM_ACTION;
  task: ChoiceTask | FormChoiceAction | VariableChoiceAction;
} => {
  return (
    isUserChoiceStep(step) ||
    isFormChoiceStep(step) ||
    isVariableChoiceStep(step)
  );
};

export const isAssessTemplatedReportForCommentsStep = (
  step: WorkflowConfigStepType
): step is WorkflowConfigStepType & {
  type: typeof WorkflowStepType.SYSTEM_ACTION;
  task: AssessTemplatedReportForCommentsAction;
} => {
  return (
    step.type === WorkflowStepType.SYSTEM_ACTION &&
    step.task.type === SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS
  );
};

export const isParallelStep = (
  step: WorkflowConfigStepType
): step is WorkflowParallelTasksType => {
  return step.type === WorkflowStepType.PARALLEL;
};

/**
 * Sets up branches for a new parallel step with two branches, each containing a starting manual step
 */
export const setupParallelStepBranches = (
  newStep: Draft<WorkflowConfigStepType>
): {
  branch1StepId: string;
  branch2StepId: string;
} => {
  const branch1StepId = `branch-step-${nanoid(8)}`;
  const branch1Step: WorkflowConfigStepType = {
    name: getWorkflowTaskNameFromType(UserTaskType.MANUAL_TASK),
    type: WorkflowStepType.USER_TASK,
    description: "Description",
    assignee: null,
    assignee_user: null,
    assignee_group: null,
    task: {
      type: UserTaskType.MANUAL_TASK,
    },
    end: true,
  } as WorkflowConfigStepType;

  const branch2StepId = `branch-step-${nanoid(8)}`;
  const branch2Step: WorkflowConfigStepType = {
    name: getWorkflowTaskNameFromType(UserTaskType.MANUAL_TASK),
    type: WorkflowStepType.USER_TASK,
    description: "Description",
    assignee: null,
    assignee_user: null,
    assignee_group: null,
    task: {
      type: UserTaskType.MANUAL_TASK,
    },
    end: true,
  } as WorkflowConfigStepType;

  (newStep as WorkflowParallelTasksType).branches = [
    {
      start: branch1StepId,
      steps: {
        [branch1StepId]: branch1Step,
      },
    },
    {
      start: branch2StepId,
      steps: {
        [branch2StepId]: branch2Step,
      },
    },
  ];

  return {
    branch1StepId,
    branch2StepId,
  };
};

export type BranchInfo = {
  willBeAddedToBranch: boolean;
  parallelStepId: string | undefined;
};

/**
 * Determines if a new step will be added to a parallel step branch and which parallel step contains it
 */
export const determineBranchInfo = (
  draft: Draft<WorkflowConfigurationType>,
  previousStepId: string
): BranchInfo => {
  const previousStep = draft.steps[previousStepId];
  const isPreviousStepParallel =
    previousStep?.type === WorkflowStepType.PARALLEL;

  const findParallelBranchContainingStep = (
    parallelStep: Draft<WorkflowParallelTasksType>,
    parallelStepId: string
  ): string | undefined => {
    for (const branch of parallelStep.branches) {
      if (branch.steps[previousStepId]) {
        return parallelStepId;
      }

      for (const [nestedStepId, nestedStep] of Object.entries(branch.steps)) {
        if (nestedStep.type !== WorkflowStepType.PARALLEL) continue;

        const nestedResult = findParallelBranchContainingStep(
          nestedStep,
          nestedStepId
        );
        if (nestedResult) return nestedResult;
      }
    }
    return undefined;
  };

  let parallelStepId: string | undefined;

  if (!isPreviousStepParallel) {
    for (const [stepId, step] of Object.entries(draft.steps)) {
      if (step.type !== WorkflowStepType.PARALLEL) continue;

      const containingParallelId = findParallelBranchContainingStep(
        step,
        stepId
      );
      if (containingParallelId) {
        parallelStepId = containingParallelId;
        break;
      }
    }
  }

  return {
    willBeAddedToBranch: !!parallelStepId || isPreviousStepParallel,
    parallelStepId:
      parallelStepId ?? (isPreviousStepParallel ? previousStepId : undefined),
  };
};

type StepTypeInfo = {
  isUserChoice: boolean;
  isFormChoice: boolean;
  isVariableChoice: boolean;
  isAssessTemplatedReportForComments: boolean;
  isParallel: boolean;
  hasChoices: boolean;
};

export const getStepTypeInfo = (
  newStep: WorkflowConfigStepType
): StepTypeInfo => {
  const isUserChoice = isUserChoiceStep(newStep);
  const isFormChoice = isFormChoiceStep(newStep);
  const isVariableChoice = isVariableChoiceStep(newStep);
  const isAssessTemplatedReportForComments =
    isAssessTemplatedReportForCommentsStep(newStep);
  const isParallel = isParallelStep(newStep);
  const hasChoices = isUserChoice || isFormChoice || isVariableChoice;

  return {
    isUserChoice,
    isFormChoice,
    isVariableChoice,
    isAssessTemplatedReportForComments,
    isParallel,
    hasChoices,
  };
};

/**
 * Creates the associated steps necessary for a new step to be valid.
 * For example, it creates two manual steps for choice steps.
 */
export const createAssociatedStepsForNewStep = (
  draft: Draft<WorkflowConfigurationType>,
  newStep: Draft<WorkflowConfigStepType>,
  previousStepId: string,
  stepTypeInfo: StepTypeInfo,
  markNodeAsDirty: (nodeId: string) => void
): void => {
  const branchStepIds: string[] = [];
  const previousStepNext = draft.steps[previousStepId]?.next;

  if (stepTypeInfo.isParallel && isParallelStep(newStep)) {
    const hasPredefinedBranches =
      Array.isArray((newStep as WorkflowParallelTasksType).branches) &&
      (newStep as WorkflowParallelTasksType).branches.length > 0;

    if (hasPredefinedBranches) {
      (newStep as WorkflowParallelTasksType).branches.forEach((branch) => {
        Object.keys(branch.steps).forEach((stepId) => {
          markNodeAsDirty(stepId);
          branchStepIds.push(stepId);
        });
      });
    } else {
      const { branch1StepId, branch2StepId } =
        setupParallelStepBranches(newStep);
      if (branch1StepId) {
        markNodeAsDirty(branch1StepId);
        branchStepIds.push(branch1StepId);
      }
      if (branch2StepId) {
        markNodeAsDirty(branch2StepId);
        branchStepIds.push(branch2StepId);
      }
    }
  } else if (
    stepTypeInfo.hasChoices ||
    stepTypeInfo.isAssessTemplatedReportForComments
  ) {
    const { manualStep1Id, manualStep2Id, manualStep1, manualStep2 } =
      createManualStepsForChoice(previousStepNext);

    draft.steps[manualStep1Id] = manualStep1;
    draft.steps[manualStep2Id] = manualStep2;

    if (manualStep1Id) {
      markNodeAsDirty(manualStep1Id);
      branchStepIds.push(manualStep1Id);
    }
    if (manualStep2Id) {
      markNodeAsDirty(manualStep2Id);
      branchStepIds.push(manualStep2Id);
    }

    if (stepTypeInfo.hasChoices) {
      setupChoiceStepChoices(
        newStep as Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>,
        manualStep1Id,
        manualStep2Id
      );
    } else if (stepTypeInfo.isAssessTemplatedReportForComments) {
      setupAssessTemplatedReportForCommentsStepNextSteps(
        newStep as Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>,
        manualStep1Id,
        manualStep2Id
      );
    }
  }
};

/**
 * Adds a new step to the workflow configuration
 * This is the main function that orchestrates adding a step
 */
export const addStepToWorkflow = (
  draft: Draft<WorkflowConfigurationType>,
  newStep: Draft<Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>>,
  newStepId: string,
  previousStepId: string,
  nextStepId: string | undefined,
  markNodeAsDirty: (nodeId: string) => void
): BranchInfo => {
  const stepTypeInfo = getStepTypeInfo(newStep);
  const branchInfo = determineBranchInfo(draft, previousStepId);

  createAssociatedStepsForNewStep(
    draft,
    newStep,
    previousStepId,
    stepTypeInfo,
    markNodeAsDirty
  );

  // Update the previous step's next property to point to the new step
  // If previous step is parallel or in a branch, the step will be added to branches (not top level)
  updatePreviousStep(
    draft,
    previousStepId,
    newStepId,
    newStep,
    nextStepId,
    stepTypeInfo.hasChoices,
    stepTypeInfo.isAssessTemplatedReportForComments,
    markNodeAsDirty
  );

  // If a new branch was added to a parallel step, mark the parallel step and branch step as dirty
  if (branchInfo.parallelStepId) {
    markNodeAsDirty(branchInfo.parallelStepId);
  }

  // Only add to top-level steps if it's not being added to parallel branches
  if (!branchInfo.willBeAddedToBranch) {
    draft.steps[newStepId] = newStep;
  }

  return branchInfo;
};

/**
 * Gets a new trigger configuration from the selection option
 */
export const getNewTriggerConfigFromSelectedOption = (
  selectedOption: WorkflowTriggerEventType & "scheduled"
) => {
  switch (selectedOption) {
    case WorkflowTriggerEvent.FORM_SUBMISSION_CREATE:
      return {
        type: WorkflowTriggerType.SYSTEM,
        event: WorkflowTriggerEvent.FORM_SUBMISSION_CREATE,
        form_schema_id: "",
        answer_filter: [],
      };
    case WorkflowTriggerEvent.ASSET_CREATE:
      return {
        type: WorkflowTriggerType.SYSTEM,
        event: WorkflowTriggerEvent.ASSET_CREATE,
        asset_type: AssetType.FACILITY,
        asset_filter: {},
      };
    case WorkflowTriggerEvent.ASSET_UPDATE:
      return {
        type: WorkflowTriggerType.SYSTEM,
        event: WorkflowTriggerEvent.ASSET_UPDATE,
        asset_type: AssetType.FACILITY,
        asset_filter: {},
      };
    case WorkflowTriggerEvent.WORKFLOW_COMPLETE:
      return {
        type: WorkflowTriggerType.SYSTEM,
        event: WorkflowTriggerEvent.WORKFLOW_COMPLETE,
        workflow_template_id: "",
      };
    case WorkflowTriggerEvent.DATASET_SUBMISSION:
      return {
        type: WorkflowTriggerType.SYSTEM,
        event: WorkflowTriggerEvent.DATASET_SUBMISSION,
        dataset_id: "",
      };
    case "scheduled":
    default:
      return {
        cron: DEFAULT_CRON,
        type: "scheduled",
      };
  }
};

/**
 * Gets a new step configuration from the selection option
 */
export const getNewStepConfigFromSelectedOption = (
  stepDetails: NewNodeDetailsType
) => {
  const isParallel = stepDetails.type === WorkflowStepType.PARALLEL;
  const baseConfig = {
    name: getWorkflowTaskNameFromType(stepDetails.taskType),
    type: stepDetails.type,
    description: "Description",
    ...(isParallel
      ? {}
      : {
          assignee: null,
          assignee_user: null,
          assignee_group: null,
        }),
  };
  let taskConfig = {};
  switch (stepDetails.taskType) {
    case UserTaskType.MANUAL_TASK:
      taskConfig = {
        task: {
          type: UserTaskType.MANUAL_TASK,
        },
      };
      break;
    case UserTaskType.SUBMIT_FORM:
      taskConfig = {
        task: {
          type: UserTaskType.SUBMIT_FORM,
          form_schema_id: "",
          autocomplete: false,
          auto_attach: false,
          form_required: false,
        },
      };
      break;
    case UserTaskType.COMPLETE_EVENT:
      taskConfig = {
        task: {
          type: UserTaskType.COMPLETE_EVENT,
          event_step_id: "",
        },
      };
      break;
    case UserTaskType.CHOICE:
      taskConfig = {
        task: {
          type: UserTaskType.CHOICE,
          choices: [],
        },
      };
      break;
    case UserTaskType.SUBMIT_DATASET:
      taskConfig = {
        task: {
          type: UserTaskType.SUBMIT_DATASET,
          dataset_id: "",
          dataset_required: false,
        },
      };
      break;
    case SystemActionType.CREATE_EVENT:
      taskConfig = {
        task: {
          type: SystemActionType.CREATE_EVENT,
          event_schema_id: "",
          event_status: EventStatus.open,
        },
      };
      break;
    case SystemActionType.FORM_CHOICE:
      taskConfig = {
        task: {
          type: SystemActionType.FORM_CHOICE,
          form_schema_id: "",
          choices: [],
        },
      };
      break;
    case SystemActionType.VARIABLE_CHOICE:
      taskConfig = {
        task: {
          type: SystemActionType.VARIABLE_CHOICE,
          choices: [],
        },
      };
      break;
    case SystemActionType.DELAY:
      taskConfig = {
        task: {
          type: SystemActionType.DELAY,
          duration: 1,
          time_period: WorkflowTriggerTimePeriod.HOURS,
        },
      };
      break;
    case SystemActionType.CREATE_FORM:
      taskConfig = {
        task: {
          type: SystemActionType.CREATE_FORM,
          form_schema_id: "",
          form_status: FormSubmissionStatus.SUBMITTED,
        },
      };
      break;
    case SystemActionType.LOCK_RECORD:
      taskConfig = {
        task: {
          type: SystemActionType.LOCK_RECORD,
          record_filter: {},
        },
      };
      break;
    case SystemActionType.UNLOCK_RECORD:
      taskConfig = {
        task: {
          type: SystemActionType.UNLOCK_RECORD,
          record_filter: {},
        },
      };
      break;
    case SystemActionType.LOCK_REPORT:
      taskConfig = {
        task: {
          type: SystemActionType.LOCK_REPORT,
          report_filter: {},
        },
      };
      break;
    case SystemActionType.UNLOCK_REPORT:
      taskConfig = {
        task: {
          type: SystemActionType.UNLOCK_REPORT,
          report_filter: {},
        },
      };
      break;
    case SystemActionType.RUN_CALCULATIONS:
      taskConfig = {
        task: {
          type: SystemActionType.RUN_CALCULATIONS,
          filter: {},
          year_months: [],
          promote_to_record: false,
          fill_year_months: false,
        },
      };
      break;
    case SystemActionType.RUN_TEMPLATED_REPORT:
      taskConfig = {
        task: {
          type: SystemActionType.RUN_TEMPLATED_REPORT,
          name: "",
          templated_report_name: "",
          report_input: {},
          enable_new_version: false,
        },
      };
      break;
    case SystemActionType.RUN_DEFAULT_RECORD_VALUE_CONFIGURATION:
      taskConfig = {
        task: {
          type: SystemActionType.RUN_DEFAULT_RECORD_VALUE_CONFIGURATION,
          fill_year_months: false,
          overwrite_all_values: false,
          seed_records: false,
          filter: {},
        },
      };
      break;
    case SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS:
      taskConfig = {
        task: {
          type: SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS,
          assessment_failure_next: "",
          assessment_success_next: "",
          header_name: "",
          report_id: "",
          styles_to_assess: ["success"],
        },
      };
      break;
    case SystemActionType.UPDATE_CUSTOM_ATTRIBUTES:
    case SystemActionType.UPDATE_ATTRIBUTES:
      taskConfig = {
        task: {
          type: SystemActionType.UPDATE_ATTRIBUTES,
          assets: [],
        },
      };
      break;
    case SystemActionType.SEND_REPORT:
      taskConfig = {
        task: {
          type: SystemActionType.SEND_REPORT,
          title: "",
          message: "",
          details: [],
        },
      };
      break;
    case SystemActionType.SEND_NOTIFICATION:
      taskConfig = {
        task: {
          type: SystemActionType.SEND_NOTIFICATION,
          title: "",
          message: "",
          details: [],
        },
      };
      break;
    case SystemActionType.RUN_WORKER:
      taskConfig = {
        task: {
          type: SystemActionType.RUN_WORKER,
          worker_id: "",
          worker_version_id: undefined,
        },
      };
      break;
    case SystemActionType.GENERIC_SYSTEM_ACTION:
      taskConfig = {
        task: {
          type: SystemActionType.GENERIC_SYSTEM_ACTION,
          operation: "",
          input: {},
          output: {},
        },
      };
      break;
    case WorkflowStepType.PARALLEL:
      taskConfig = {
        branches: [],
      };
      break;
  }

  return {
    ...baseConfig,
    ...taskConfig,
  };
};
